import React, { useState, useRef, useEffect } from 'react'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import Typography from '@mui/material/Typography'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { getAllStaffEmployees } from 'services/api/modules/staff-management/staffManagementDashboard'
import { postHeadOfOrganization } from 'services/api/department-configuration'
import defaultAvatar from 'assets/images/default_avatar.png'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'

const ManageHeadOfOrganizationModal = ({
  showManageHeadOfOrganizationModal,
  handleCloseManageHeadOfOrganizationModal,
  headData,
}) => {
  const queryClient = useQueryClient()

  const [selectValue, setSelectValue] = useState('')
  const [dropdownFilterOptions, setDropdownFilterOptions] = useState()
  const formikRef = useRef()

  const allStaffEmployees = useQuery('allStaffEmployees', async () => {
    const { data } = await getAllStaffEmployees()
    return Object.values(data.staff).flat()
  })

  const mutation = useMutation(postHeadOfOrganization, {
    onSuccess: async () => {
      await queryClient.invalidateQueries('departmentList')
      handleCloseModal()
    },
    onError: (e) => {
      if (e.status === 422) {
        handleValidationErrors(e, formikRef)
      } else {
        console.log(e?.data?.message)
      }
    },
  })

  const handleOnSubmit = async (values) => {
    await mutation.mutate(values)
  }

  const handleSetSelectValue = (event) => {
    const val = event?.target?.value || ''
    setSelectValue(val)

    // ✅ guard: val might be '' (cleared)
    formikRef.current?.setFieldValue?.('staff_id', val?.id ?? '')
  }

  const handleCloseModal = () => {
    handleCloseManageHeadOfOrganizationModal()
    setSelectValue('')
    formikRef.current?.resetForm?.()
  }

  useEffect(() => {
    // ✅ Only run when modal opens, and only if we have staff list
    if (!showManageHeadOfOrganizationModal) return
    if (!allStaffEmployees?.data?.length) return

    const headId = headData?.head?.id // ✅ head may be null
    if (!headId) {
      setSelectValue('')
      formikRef.current?.setFieldValue?.('staff_id', '')
      return
    }

    const selected = allStaffEmployees.data.find((item) => item?.id === headId) || ''
    setSelectValue(selected)
    formikRef.current?.setFieldValue?.('staff_id', headId)
  }, [showManageHeadOfOrganizationModal, allStaffEmployees?.data, headData?.head?.id])

  return (
    <AdxFormModal
      title="Head of organization"
      subtitle=""
      open={showManageHeadOfOrganizationModal}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
    >
      <Formik
        enableReinitialize
        onSubmit={(values, actions) => handleOnSubmit(values, actions)}
        innerRef={formikRef}
        initialValues={{
          // ✅ head may be null
          staff_id: headData?.head?.id ?? '',
        }}
      >
        {({ errors, touched, handleSubmit }) => (
          <Form>
            <Typography color="light-gray-text.main" mb={2}>
              The Head of Organization (HOQ) is at the top layer of the of the organization. They
              oversee all approvals including both employees and managers.
            </Typography>

            <Field
              name="staff_id"
              as={AdxDropdownWithFilter}
              label="Head of organization"
              allOptions={allStaffEmployees?.data}
              setOptions={setDropdownFilterOptions}
              filters={['name']}
              sx={{ width: '100%', marginBottom: 8 }}
              value={selectValue}
              onChange={handleSetSelectValue}
              isErrorShown={errors.staff_id && touched.staff_id}
              errorMessage={<ErrorMessage name="staff_id" component={AdxErrorMessage} />}
              renderValue={(option) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      backgroundColor: 'gray-background.main',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      mx: 1,
                    }}
                  >
                    <Avatar src={option?.profile_image_url || defaultAvatar} />
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body1">{option?.name ?? 'Select staff'}</Typography>
                    <Typography variant="caption">{option?.role}</Typography>
                  </Box>
                </Box>
              )}
            >
              {dropdownFilterOptions?.map((item) => (
                <AdxMenuItem key={item?.id} value={item} selectedvalue={selectValue}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        backgroundColor: 'gray-background.main',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        mx: 1,
                      }}
                    >
                      <Avatar src={item?.profile_image_url || defaultAvatar} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="body1">{item?.name}</Typography>
                    </Box>
                  </Box>
                </AdxMenuItem>
              ))}
            </Field>

            <AdxButton fullWidth onClick={handleSubmit} disabled={mutation.isLoading}>
              Save
            </AdxButton>
          </Form>
        )}
      </Formik>
    </AdxFormModal>
  )
}

export default ManageHeadOfOrganizationModal
